import CryptoJS from 'crypto-js';
import { supabase } from './supabase';

export interface EncryptionResult {
  encryptedData: string;
  key: string;
  iv: string;
}

export interface DecryptionParams {
  encryptedData: string;
  key: string;
  iv: string;
}

/**
 * Generate a random 256-bit encryption key
 */
export const generateEncryptionKey = (): string => {
  return CryptoJS.lib.WordArray.random(256/8).toString();
};

/**
 * Generate a random initialization vector
 */
export const generateIV = (): string => {
  return CryptoJS.lib.WordArray.random(128/8).toString();
};

/**
 * Encrypt file data using AES-256-CBC
 */
export const encryptFile = async (file: File): Promise<EncryptionResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
        
        const key = generateEncryptionKey();
        const iv = generateIV();
        
        const encrypted = CryptoJS.AES.encrypt(wordArray, key, {
          iv: CryptoJS.enc.Hex.parse(iv),
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
        });
        
        resolve({
          encryptedData: encrypted.toString(),
          key,
          iv
        });
      } catch (error) {
        reject(new Error('Encryption failed: ' + error));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Decrypt file data using AES-256-CBC
 */
export const decryptFile = (params: DecryptionParams): ArrayBuffer => {
  try {
    const decrypted = CryptoJS.AES.decrypt(params.encryptedData, params.key, {
      iv: CryptoJS.enc.Hex.parse(params.iv),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    
    // Convert WordArray to ArrayBuffer
    const typedArray = new Uint8Array(decrypted.sigBytes);
    for (let i = 0; i < decrypted.sigBytes; i++) {
      typedArray[i] = (decrypted.words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    }
    
    return typedArray.buffer;
  } catch (error) {
    throw new Error('Decryption failed: ' + error);
  }
};

/**
 * Retry mechanism for fetching user's public key
 */
const getUserPublicKeyWithRetry = async (userId: string, maxRetries: number = 3, delay: number = 1000): Promise<string | null> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const { data: userProfile, error: profileError } = await supabase
        .from('users')
        .select('public_key')
        .eq('id', userId)
        .single();
        
      if (!profileError && userProfile?.public_key) {
        return userProfile.public_key;
      }
      
      if (profileError && profileError.code !== 'PGRST116') {
        // If it's not a "no rows" error, throw it
        throw profileError;
      }
      
      // If this is not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        console.log(`Attempt ${attempt} failed to get user public key, retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 1.5; // Exponential backoff
      }
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }
      console.log(`Attempt ${attempt} failed with error:`, error);
      await new Promise(resolve => setTimeout(resolve, delay));
      delay *= 1.5;
    }
  }
  
  return null;
};

/**
 * Store encryption key securely in localStorage and in the database (encrypted)
 */
export const storeEncryptionKey = async (documentId: string, key: string, iv: string): Promise<void> => {
  try {
    // Store locally for immediate use
    const keyData = {
      key,
      iv,
      timestamp: new Date().toISOString(),
      documentId
    };
    
    localStorage.setItem(`aegis_key_${documentId}`, JSON.stringify(keyData));
    
    // Get user data - this is critical for database storage
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error('Failed to get user for key storage: ' + (userError?.message || 'No user found'));
    }
    
    // Get user's public key with retry mechanism - this must succeed
    const publicKeyString = await getUserPublicKeyWithRetry(userData.user.id);
    
    if (!publicKeyString) {
      throw new Error('Failed to get user public key after retries. Cannot store encryption key securely.');
    }
    
    // Import user's public key and encrypt the keys
    const { importPublicKey, encryptKeysForRecipient } = await import('./crypto');
    const publicKey = await importPublicKey(publicKeyString);
    
    // Encrypt keys for the user
    const encryptedKeyData = await encryptKeysForRecipient(key, iv, publicKey);
    
    // Store in document_keys table with upsert to handle duplicates
    const { error: insertError } = await supabase
      .from('document_keys')
      .upsert({
        document_id: documentId,
        user_id: userData.user.id,
        encrypted_key: encryptedKeyData.encryptedKey,
        key_fingerprint: encryptedKeyData.recipientPublicKeyFingerprint,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'document_id,user_id'
      });
      
    if (insertError) {
      throw new Error('Failed to store encryption key in database: ' + insertError.message);
    }
    
    console.log('Successfully stored encryption key in database for document:', documentId);
  } catch (error) {
    // Remove from localStorage if database storage failed to maintain consistency
    try {
      localStorage.removeItem(`aegis_key_${documentId}`);
    } catch (cleanupError) {
      console.error('Failed to cleanup localStorage after database error:', cleanupError);
    }
    
    throw new Error('Failed to store encryption key: ' + (error instanceof Error ? error.message : String(error)));
  }
};

/**
 * Retrieve encryption key from localStorage or database
 */
export const getEncryptionKey = async (documentId: string): Promise<{ key: string; iv: string } | null> => {
  try {
    // First try localStorage
    const stored = localStorage.getItem(`aegis_key_${documentId}`);
    if (stored) {
      const keyData = JSON.parse(stored);
      return {
        key: keyData.key,
        iv: keyData.iv
      };
    }
    
    // If not in localStorage, try database
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      console.error('Failed to get user for key retrieval:', userError);
      return null;
    }
    
    // Get encrypted key from database
    const { data: keyData, error: keyError } = await supabase
      .from('document_keys')
      .select('encrypted_key, key_fingerprint')
      .eq('document_id', documentId)
      .eq('user_id', userData.user.id)
      .maybeSingle(); // Use maybeSingle instead of single to avoid PGRST116 error
      
    // If no key found, return null
    if (keyError || !keyData || !keyData.encrypted_key) {
      console.log('No encryption key found in database for document:', documentId);
      return null;
    }
    
    // Get user's private key
    const { getUserKeyPair, decryptKeysFromSender } = await import('./crypto');
    const keyPair = await getUserKeyPair();
    
    if (!keyPair) {
      console.error('Failed to get user key pair');
      return null;
    }
    
    // Decrypt the key
    const decryptedKeys = await decryptKeysFromSender(
      {
        encryptedKey: keyData.encrypted_key,
        encryptedIv: '', // Not used in RSA
        recipientPublicKeyFingerprint: keyData.key_fingerprint
      },
      keyPair.privateKey
    );
    
    // Store in localStorage for future use
    const keyDataForStorage = {
      key: decryptedKeys.key,
      iv: decryptedKeys.iv,
      timestamp: new Date().toISOString(),
      documentId
    };
    
    localStorage.setItem(`aegis_key_${documentId}`, JSON.stringify(keyDataForStorage));
    
    return decryptedKeys;
  } catch (error) {
    console.error('Failed to retrieve encryption key:', error);
    return null;
  }
};

/**
 * Remove encryption key from localStorage
 */
export const removeEncryptionKey = (documentId: string): void => {
  try {
    localStorage.removeItem(`aegis_key_${documentId}`);
  } catch (error) {
    console.error('Failed to remove encryption key:', error);
  }
};

/**
 * Validate file signature to prevent malicious uploads
 */
export const validateFileSignature = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const arrayBuffer = event.target?.result as ArrayBuffer;
      const uint8Array = new Uint8Array(arrayBuffer.slice(0, 4));
      const header = Array.from(uint8Array).map(b => b.toString(16).padStart(2, '0')).join('');
      
      // Common file signatures
      const signatures: Record<string, string[]> = {
        'pdf': ['25504446'],
        'png': ['89504e47'],
        'jpg': ['ffd8ffe0', 'ffd8ffe1', 'ffd8ffe2'],
        'gif': ['47494638'],
        'zip': ['504b0304'],
        'docx': ['504b0304'], // DOCX is a ZIP file
        'xlsx': ['504b0304'], // XLSX is a ZIP file
        'txt': [] // Text files don't have a consistent signature
      };
      
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
      const expectedSignatures = signatures[fileExtension];
      
      if (!expectedSignatures || expectedSignatures.length === 0) {
        // For files without signatures (like txt), allow them
        resolve(true);
        return;
      }
      
      const isValid = expectedSignatures.some(sig => header.startsWith(sig));
      resolve(isValid);
    };
    
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.slice(0, 4));
  });
};